import { motion } from "framer-motion";
import {
  Archive,
  PauseCircle,
  ArrowUpRight,
  RefreshCcw,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const migrationInfoIcons = [Archive, PauseCircle, ArrowUpRight, RefreshCcw];

const ExistingGroupsSection = () => {
  const { t } = useTranslation();

  return (
    <section className="pt-20" id="products">
      <div className="text-gray-900 dark:text-white">
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-6 ">
            {t("migrationPage.migration.title")}
          </h2>
          <p className="text-gray-600 dark:text-white/70 max-w-2xl text-lg">
            {t("migrationPage.migration.description")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12  mx-auto">
          {/* Information Cards */}
          <div className="space-y-6">
            {Array.from({ length: 4 }, (_, i) => i + 1).map((num) => (
              <MigrationInfoCard
                key={num}
                icon={migrationInfoIcons[num - 1]}
                title={t(`migrationPage.migration.step${num}.name`)}
                description={t(
                  `migrationPage.migration.step${num}.description`,
                )}
              />
            ))}
          </div>

          {/* Visual Transformation Mockup */}
          <div className="bg-defaultGreen/10 dark:bg-defaultGreen/20 shadow-md rounded-xl px-2.5 py-8 md:p-16 flex flex-col items-center justify-center">
            {/* Archived State */}
            <div className="w-full max-w-sm  rounded-2xl shadow-md bg-white dark:bg-defaultBlack p-5 mb-8 border dark:border-none">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-defaultGreen rounded-full text-white">
                    <Users size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Family Savings</h4>
                    <p className="text-xs text-gray-400">12 members</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-red-500 uppercase px-2 py-1 rounded">
                  Legacy
                </span>
              </div>
              <div className="flex justify-between items-end border-t border-black/5 dark:border-white/5 pt-3">
                <p className="text-xs text-gray-400">Total Saved</p>
                <p className="text-xs font-bold">850,000 RWF</p>
              </div>
            </div>

            {/* Sync Icon */}
            <motion.div
              className="bg-defaultGreen text-white p-3 rounded-full shadow-lg z-10 -my-4 mb-4 animate-spin-slow"
              initial="hidden"
              viewport={{ once: true }}
              variants={{
                hidden: { rotate: 0 },
                visible: {
                  rotate: 360,
                  y: 0,
                  transition: {
                    delay: 0.4,
                    duration: 0.7,
                    ease: "easeOut",
                  },
                },
              }}
              whileInView="visible"
            >
              <RefreshCcw size={24} />
            </motion.div>

            {/* Active State */}
            <motion.div
              className="w-full max-w-sm  rounded-2xl shadow-md bg-white dark:bg-defaultBlack p-5 border-2 border-emerald-600 "
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.6,
                    duration: 0.7,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-600 rounded-full text-white">
                    <Users size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Family Savings</h4>
                    <p className="text-xs text-gray-400">12 members</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-emerald-600  px-2 py-1 rounded uppercase tracking-wider">
                  Active
                </span>
              </div>
              <p className="text-xs text-emerald-700 font-medium">
                Same rules • Same members
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExistingGroupsSection;

// --- Reuseable component ---

const MigrationInfoCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="flex items-start gap-4 p-5 bg-white dark:bg-white/5 shadow-md rounded-lg border border-defaultBlack/5"
    variants={{
      hidden: { opacity: 0, y: 40 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { delay: 0.2, duration: 0.7, ease: "easeOut" },
      },
    }}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
  >
    <div className="p-3 bg-defaultGreen/80 text-white rounded-lg">
      <Icon size={20} />
    </div>
    <div>
      <h3 className="font-bold uppercase  tracking-wider mb-2 text-gray-900 dark:text-white">
        {title}
      </h3>
      <p className="text-gray-600 dark:text-white/70 leading-relaxed">
        {description}
      </p>
    </div>
  </motion.div>
);
